// src/pages/WorkExperience.tsx
import React, { useEffect, useState } from 'react';
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';
import { MdOutlineWork as WorkIcon } from 'react-icons/md';
import { FaStar as StarIcon, FaPlay, FaCode, FaServer, FaFlask, FaChartLine, FaDatabase } from 'react-icons/fa';
import { SiTensorflow, SiPytorch, SiGooglecloud, SiOpenai, SiPython, SiSelenium, SiPandas, SiReact, SiNodedotjs } from 'react-icons/si';
import './WorkExperience.css';
import { TimelineItem } from '../types';
import { getTimeline } from '../queries/getTimeline';

// Extend TimelineItem for our UI needs 
type ExtendedTimelineItem = TimelineItem & {
  experienceType?: 'research' | 'internship' | 'featured';
};

// Updated fallback data in chronological order
const FALLBACK_DATA: ExtendedTimelineItem[] = [
  {
    timelineType: 'work',
    experienceType: 'featured',
    name: 'Parafin',
    title: 'Software Engineer - Backend (Spends team)',
    techStack: 'Python, Scala, Backend Systems, APIs, Data Modeling',
    summaryPoints: [
      'Building and improving backend systems for the Spends team, focused on reliability, correctness, and scalability',
      'Implementing features and services in Python and Scala with an emphasis on clean interfaces and observability',
      'Collaborating cross-functionally to ship impactful improvements while maintaining strong operational standards'
    ],
    dateRange: 'February 2026 - Present'
  },
  {
    timelineType: 'work',
    experienceType: 'featured',
    name: 'Scale AI',
    title: 'Gen AI Technical Advisor',
    techStack: 'Python, LLMs, Code Generation, Testing Frameworks, FAISS, RAG',
    summaryPoints: [
      'Enhanced large language model code generation accuracy by 12% through comprehensive test expansion and refined evaluation criteria across multiple programming languages',
      'Developed and maintained Python-based evaluation infrastructure with isolated testing environments, processing 500+ automated test cases and reducing manual review overhead by 18%',
      'Engineered retrieval-augmented analysis pipeline using chunked data processing and FAISS indexing to streamline multi-gigabyte log analysis, cutting ticket resolution time by 20%',
      'Collaborated with ML teams to refine model performance through systematic testing and feedback implementation'
    ],
    dateRange: 'May 2025 - December 2025'
  },
  {
    timelineType: 'work',
    experienceType: 'internship',
    name: 'M8 Social Platform',
    title: 'Lead Backend Developer',
    techStack: 'Node.js, AWS, OAuth, RBAC, Redis, Database Optimization',
    summaryPoints: [
      'Boosted API performance by 15% achieving 1,440 requests/second throughput with sub-180ms p95 latency through strategic routing optimization and connection pool management',
      'Deployed OAuth authentication and role-based access control across AWS infrastructure, decreasing authentication-related support requests by 25% while streamlining security audits',
      'Enhanced database performance through query optimization and Redis caching implementation, achieving 22% faster API response times',
      'Led backend architecture decisions and coordinated with frontend teams to ensure seamless user experience across the platform'
    ],
    dateRange: 'August 2024 - May 2025'
  },
  {
    timelineType: 'work',
    experienceType: 'internship',
    name: 'Voodies',
    title: 'Full Stack AI Engineer',
    techStack: 'React, Node.js, Multi-agent Systems, RAG, AWS, Docker, Jenkins',
    summaryPoints: [
      'Architected multi-agent AI workflow for automated content generation, utilizing RAG prompts and API integrations to reduce product description creation time from 30 to 12 minutes',
      'Developed comprehensive React/Node.js management dashboard featuring role-based permissions, JWT authentication, and RESTful APIs, enabling non-technical team members to publish content in under 3 minutes',
      'Containerized application services and established AWS EC2 deployment pipeline with Docker and Jenkins CI/CD, streamlining deployment process from 45 to 30 minutes',
      'Integrated AI-powered content classification and quality assurance systems to maintain consistent product catalog standards'
    ],
    dateRange: 'August 2025 - October 2025'
  },
  {
    timelineType: 'work',
    experienceType: 'research',
    name: 'Penn State AI Innovation Lab',
    title: 'Product Recommendation System Researcher',
    techStack: 'BERT4Rec, Sentence Transformers, LLMs, Google Search API, Python, PyTorch',
    summaryPoints: [
      'Developed a novel product recommendation system using BERT4Rec model and sentence transformers',
      'Implemented LLM-based product ideation algorithm to generate unique product concepts',
      'Created system to evaluate product uniqueness and feasibility using Google Search API',
      'Achieved 78% accuracy in predicting successful product-market fit for new product concepts',
      'Published findings in university research journal'
    ],
    dateRange: 'January 2025 - May 2025'
  },
  {
    timelineType: 'work',
    experienceType: 'research',
    name: 'Penn State Data Science Lab',
    title: 'Environmental Policy Research Analyst',
    techStack: 'Python, Selenium, Pandas, Data Mining, NLP, Statistical Analysis',
    summaryPoints: [
      'Designed and implemented a novel Environmental Policy Uncertainty Index using data mining techniques',
      'Developed automated web scrapers using Python and Selenium to collect policy data from governmental sources',
      'Applied natural language processing to quantify policy uncertainty across different sectors',
      'Created visualization dashboards to track policy changes and their impact on environmental metrics',
      'Collaborated with environmental scientists to validate index accuracy with 92% confidence level'
    ],
    dateRange: 'September 2024 - January 2025'
  },
  {
    timelineType: 'work',
    experienceType: 'internship',
    name: 'Alchem International',
    title: 'Software Engineering Intern',
    techStack: 'Python, Django, PostgreSQL, React, CI/CD, Microservices',
    summaryPoints: [
      'Contributed to monolithic architecture migration by transitioning key modules to Python/Django microservices, helping achieve 15% reduction in deployment-related issues',
      'Optimized database performance through PostgreSQL query restructuring and composite indexing, delivering 18% improvement in query execution speed',
      'Enhanced API responsiveness through multithreading implementation and strategic caching, while improving React frontend efficiency to reduce response times by 20%',
      'Integrated automated testing and deployment workflows using Python scripting for continuous integration processes'
    ],
    dateRange: 'May 2024 - August 2024'
  },
  {
    timelineType: 'work',
    experienceType: 'research',
    name: 'Penn State Healthcare Innovation Lab',
    title: 'Medical LLM Optimization Researcher',
    techStack: 'Python, PyTorch, TensorFlow, NLP, Data Privacy',
    summaryPoints: [
      'Developing compact, privacy-focused language models for medical devices',
      'Achieved 80% size reduction with 95% transcription accuracy',
      'Optimized neural network architectures for medical applications',
      'Implemented efficient data compression techniques'
    ],
    dateRange: 'January 2024 - May 2024'
  },
  {
    timelineType: 'work',
    experienceType: 'internship',
    name: 'Penn State University',
    title: 'Learning Assistant Program Lead',
    techStack: 'Teaching, Leadership, Curriculum Development, Mentoring',
    summaryPoints: [
      'Spearheaded peer tutoring program supporting 200+ students in STEM courses',
      'Coordinated a team of 15 learning assistants across multiple departments',
      'Developed innovative teaching methodologies for complex technical concepts',
      'Created comprehensive study materials and practice assessments'
    ],
    dateRange: 'August 2023 - May 2024'
  }
];

// Experience category definitions with icons
const EXPERIENCE_CATEGORIES = {
  'featured': { icon: <FaCode />, label: 'Featured Roles' },
  'research': { icon: <FaFlask />, label: 'Research Projects' },
  'internship': { icon: <FaServer />, label: 'Internships' }
};

// Tech icons for visualization
const TECH_ICONS: Record<string, JSX.Element> = {
  'AI/ML': <SiTensorflow color="#FF6F00" />,
  'PyTorch': <SiPytorch color="#EE4C2C" />,
  'LLMs': <SiOpenai color="#10a37f" />,
  'Google': <SiGooglecloud color="#4285F4" />,
  'Python': <SiPython color="#3776AB" />,
  'Selenium': <SiSelenium color="#43B02A" />,
  'Pandas': <SiPandas color="#150458" />,
  'React': <SiReact color="#61DAFB" />,
  'Node.js': <SiNodedotjs color="#339933" />,
  'Data Mining': <FaDatabase color="#FF9900" />,
  'NLP': <SiOpenai color="#10a37f" />,
  'Statistical': <FaChartLine color="#2C73D2" />
};

const COMPANY_META: Record<string, { url: string; logoSrc: string }> = {
  'Parafin': { url: 'https://www.parafin.com/', logoSrc: '/Parafin.png' },
  'Scale AI': { url: 'https://scale.com/', logoSrc: '/Scale.png' }
};

const getCompanyMeta = (companyName: string) => {
  return COMPANY_META[companyName] || null;
};

const WorkExperience: React.FC = () => {
  const [timeLineData, setTimeLineData] = useState<ExtendedTimelineItem[] | null>(null);
  const [viewMode, setViewMode] = useState<'all' | 'research' | 'internship'>('all');
  const [highlightedItem, setHighlightedItem] = useState<string | null>(null);

  useEffect(() => {
    async function fetchTimelineItem() {
      try {
        const data = await getTimeline();
        // Convert original data to our extended format and filter education
        const workOnly = data
          .filter(item => item.timelineType === 'work')
          .map(item => {
            // Assign appropriate experienceType based on position title/name
            let experienceType: 'research' | 'internship' | 'featured' = 'internship';
            
            if (item.name === "Parafin" || item.name === "Scale AI") {
              experienceType = 'featured';
            } else if (
              item.name.includes("Innovation Lab") || 
              item.name.includes("Research") ||
              item.title.includes("Researcher") ||
              item.name.includes("Data Science")
            ) {
              experienceType = 'research';
            } else if (
              item.title.includes("Intern") || 
              item.name.includes("University") ||
              item.name === "Voodies" ||
              item.name === "Alchem International"
            ) {
              experienceType = 'internship';
            }
            
            return {
              ...item,
              experienceType
            };
          })
          // Sort by date (most recent first)
          .sort((a, b) => {
            // Extract year from dateRange for sorting
            const getStartYear = (dateRange: string) => {
              if (dateRange.includes("Present")) return new Date().getFullYear();
              const parts = dateRange.split(" - ");
              const startPart = parts[0];
              const year = startPart.split(" ").pop();
              return parseInt(year || "0");
            };
            
            const getStartMonth = (dateRange: string) => {
              const monthMap: Record<string, number> = {
                'January': 1, 'February': 2, 'March': 3, 'April': 4, 'May': 5, 'June': 6,
                'July': 7, 'August': 8, 'September': 9, 'October': 10, 'November': 11, 'December': 12
              };
              const parts = dateRange.split(" - ");
              const startPart = parts[0];
              const month = startPart.split(" ")[0];
              return monthMap[month] || 0;
            };
            
            const aYear = getStartYear(a.dateRange);
            const bYear = getStartYear(b.dateRange);
            
            if (aYear !== bYear) {
              return bYear - aYear; // Most recent year first
            }
            
            return getStartMonth(b.dateRange) - getStartMonth(a.dateRange); // Most recent month first
          });
          
        setTimeLineData(workOnly);
      } catch (error) {
        console.warn("Error fetching timeline data, using fallback data:", error);
        setTimeLineData(FALLBACK_DATA);
      }
    }
    fetchTimelineItem();
  }, []);

  if (!timeLineData) return <div className="loading">Loading...</div>;

  // Create focused items (Trending in Your Career) — all items marked as featured
  const focusedItems = timeLineData.filter(item => item.experienceType === 'featured');

  // Filter remaining items based on view mode (excluding featured items so they only appear in trending)
  const remainingItems = timeLineData.filter(item => 
    item.experienceType !== 'featured' &&
    (viewMode === 'all' || item.experienceType === viewMode)
  );

  // Create tech tags from experience tech stacks
  const createTechTags = (techStack: string) => {
    return techStack.split(', ').map(tech => {
      for (const [key, icon] of Object.entries(TECH_ICONS)) {
        if (tech.includes(key)) {
          return { tech, icon };
        }
      }
      return { tech, icon: null };
    });
  };

  return (
    <div className="netflix-experience-container">
      {/* Banner section */}
      <div className="experience-banner">
        <div className="banner-content">
          <h1 className="netflix-title">PROFESSIONAL JOURNEY</h1>
          
          <div className="netflix-meta">
            <span className="match">98% Match</span>
            <span className="year">2025</span>
            <span className="rating">PRO</span>
            <span className="duration">Full-time Ready</span>
          </div>

          <p className="banner-description">
            From AI research to software development leadership, explore my professional journey. 
            Currently a Backend Software Engineer at Parafin on the Spends team, working primarily in Python and Scala.
          </p>

          <div className="banner-buttons">
            <button 
              className={`btn-play ${viewMode === 'all' ? 'active' : ''}`} 
              onClick={() => setViewMode('all')}
            >
              <FaPlay />
              <span>All Experiences</span>
            </button>
            <button 
              className={`btn-category ${viewMode === 'research' ? 'active' : ''}`} 
              onClick={() => setViewMode('research')}
            >
              {EXPERIENCE_CATEGORIES.research.icon}
              <span>Research</span>
            </button>
            <button 
              className={`btn-category ${viewMode === 'internship' ? 'active' : ''}`} 
              onClick={() => setViewMode('internship')}
            >
              {EXPERIENCE_CATEGORIES.internship.icon}
              <span>Internships</span>
            </button>
          </div>
        </div>
      </div>

      {/* Highlighted experiences (Trending in Your Career) */}
      <section className="highlighted-section">
        <h2 className="netflix-category-title">Trending in Your Career</h2>
        
        <div className="highlighted-grid">
          {focusedItems.map((item, index) => (
            <div 
              key={`highlight-${index}`} 
              className={`highlight-card ${highlightedItem === item.name ? 'expanded' : ''}`}
              onClick={() => setHighlightedItem(highlightedItem === item.name ? null : item.name)}
            >
              <div className="highlight-header" style={{
                backgroundImage: item.name === "Parafin"
                  ? 'linear-gradient(to right, #1f2937, #0f172a)'
                  : item.name === "Scale AI" 
                  ? 'linear-gradient(to right, #2c3e50, #4a5568)' 
                  : 'linear-gradient(to right, #2d3436, #636e72)'
                ,
                ...(getCompanyMeta(item.name)
                  ? ({ ['--company-logo' as any]: `url(${getCompanyMeta(item.name)!.logoSrc})` } as React.CSSProperties)
                  : {})
                ,
                ...(item.name === "Parafin"
                  ? ({
                      ['--company-logo-opacity' as any]: 0.26,
                      ['--company-logo-opacity-hover' as any]: 0.38,
                      ['--company-logo-size' as any]: '230px',
                      ['--company-logo-size-hover' as any]: '280px'
                    } as React.CSSProperties)
                  : {})
              }}>
                <div className="highlight-top">
                  <div className="highlight-left">
                    <span className="highlight-type">
                      {item.name === "Parafin" ? "FINTECH" : item.name === "Scale AI" ? "AI INNOVATION" : "STARTUP"}
                    </span>
                  </div>
                </div>
                <h3 className="highlight-title">{item.title}</h3>
                {getCompanyMeta(item.name) ? (
                  <a
                    className="highlight-company company-link"
                    href={getCompanyMeta(item.name)!.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {item.name}
                  </a>
                ) : (
                  <p className="highlight-company">{item.name}</p>
                )}
                <p className="highlight-date">{item.dateRange}</p>
              </div>
              
              <div className="highlight-tech-stack">
                {createTechTags(item.techStack).map((tag, idx) => (
                  <span key={idx} className="tech-tag">
                    {tag.icon && <span className="tech-icon">{tag.icon}</span>}
                    {tag.tech}
                  </span>
                ))}
              </div>
              
              <div className="highlight-content">
                <h4 className="content-title">Responsibilities</h4>
                <ul className="responsibility-list">
                  {Array.isArray(item.summaryPoints) ? (
                    item.summaryPoints.map((point, idx) => (
                      <li key={idx} className="responsibility-item">{point}</li>
                    ))
                  ) : (
                    <li className="responsibility-item">{item.summaryPoints}</li>
                  )}
                </ul>
              </div>
              
              <div className="highlight-footer">
                <div className="highlight-footer-actions">
                  <button className="more-info-btn">
                    {highlightedItem === item.name ? 'Show Less' : 'More Info'}
                  </button>
                  {getCompanyMeta(item.name) && (
                    <a
                      className="company-visit bottom"
                      href={getCompanyMeta(item.name)!.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Visit site
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Netflix-style category row */}
      <div className="netflix-category">
        <h2 className="netflix-category-title">
          {viewMode === 'all' ? 'More Professional Experience' : 
           viewMode === 'research' ? 'Research Projects' : 'Internships'}
        </h2>
      </div>

      {/* Timeline content for other experiences */}
      <div className="timeline-content">
        <VerticalTimeline>
          {remainingItems.map((experience, index) => (
            <VerticalTimelineElement
              key={index}
              className="netflix-card"
              contentStyle={{
                background: '#1a1a1a',
                boxShadow: '0 4px 16px rgba(99, 99, 99, 0.3)',
                border: '1px solid rgba(99, 99, 99, 0.3)',
                borderRadius: '8px',
                color: '#fff'
              }}
              contentArrowStyle={{
                borderRight: '7px solid #1a1a1a'
              }}
              date={experience.dateRange}
              iconStyle={{
                background: experience.experienceType === 'research' 
                  ? '#3949ab' // Subdued blue for research
                  : '#455a64', // Dark blue-grey for others
                color: '#fff',
                boxShadow: '0 0 0 4px rgba(99, 99, 99, 0.3)'
              }}
              icon={experience.experienceType === 'research' ? <FaFlask /> : <WorkIcon />}
            >
              <div className="experience-wrapper">
                <div className="experience-type-badge" style={{
                  backgroundColor: experience.experienceType === 'research' ? '#3949ab' : '#455a64'
                }}>
                  {experience.experienceType === 'research' ? 'RESEARCH' : 'INTERNSHIP'}
                </div>
                
                <h3 className="experience-title">{experience.title}</h3>
                {getCompanyMeta(experience.name) ? (
                  <a
                    className="experience-company company-link"
                    href={getCompanyMeta(experience.name)!.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {experience.name}
                  </a>
                ) : (
                  <h4 className="experience-company">{experience.name}</h4>
                )}
                
                <div className="tech-tags">
                  {createTechTags(experience.techStack).map((tag, idx) => (
                    <span key={idx} className="tech-tag small">
                      {tag.icon && <span className="tech-icon">{tag.icon}</span>}
                      {tag.tech}
                    </span>
                  ))}
                </div>
                
                <div className="experience-points">
                  {Array.isArray(experience.summaryPoints) ? 
                    experience.summaryPoints.map((point, idx) => (
                      <div key={idx} className="experience-point">
                        <span className="point-bullet">•</span>
                        <span className="point-text">{point}</span>
                      </div>
                    )) :
                    <div className="experience-point">
                      <span className="point-bullet">•</span>
                      <span className="point-text">{experience.summaryPoints}</span>
                    </div>
                  }
                </div>
              </div>
            </VerticalTimelineElement>
          ))}
          
          <VerticalTimelineElement
            iconStyle={{ 
              background: '#546e7a', 
              color: '#fff',
              boxShadow: '0 0 0 4px rgba(99, 99, 99, 0.3)'
            }}
            icon={<StarIcon />}
          />
        </VerticalTimeline>
      </div>
    </div>
  );
};

export default WorkExperience;